import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import icClose from '@iconify/icons-ic/twotone-close';
import icWarning from '@iconify/icons-ic/twotone-warning';
import { DefaultSupervisionService } from '../../../../services/default-supervision.service';
import { UserProfileService } from '../../../../../pages/application/management/user-profile/user-profile.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import {
  DefaultSupervisionAssignableContact,
  DefaultSupervisionDependencies,
  DefaultSupervisionPropagationModeEnum,
} from '../../../../models/default-supervision.model';

export interface DefaultSupervisionContactReplacementModalData {
  contact: { _id: string; firstName: string; lastName: string };
  dependencies: DefaultSupervisionDependencies;
  establishmentId?: string;
}

/**
 * Modale affichée à la suppression d'un contact référencé comme acteur
 * « Supervision métier » par défaut (PRED-3061) : affichage informatif des impacts
 * puis deux choix — remplacer par un autre contact ou retirer les affectations.
 */
@Component({
  selector: 'default-supervision-contact-replacement-modal',
  templateUrl: './default-supervision-contact-replacement-modal.component.html',
})
export class DefaultSupervisionContactReplacementModalComponent implements OnInit {
  icClose = icClose;
  icWarning = icWarning;

  form: UntypedFormGroup;
  contacts$: Observable<DefaultSupervisionAssignableContact[]>;
  loading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DefaultSupervisionContactReplacementModalData,
    public dialogRef: MatDialogRef<DefaultSupervisionContactReplacementModalComponent>,
    private defaultSupervisionService: DefaultSupervisionService,
    private userProfileService: UserProfileService,
    private snackbarService: SnackbarService,
  ) {}

  get contactName(): string {
    return `${this.data.contact.firstName} ${this.data.contact.lastName}`;
  }

  propagationModeEnum = DefaultSupervisionPropagationModeEnum;

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      choice: new UntypedFormControl('replace'),
      contact: new UntypedFormControl(null),
      // ONLY_DEFAULT : n'agit que sur les affectations par défaut (les Metrik à superviseur
      // saisi manuellement sont conservées). ALL : efface aussi ces saisies manuelles.
      propagationMode: new UntypedFormControl(DefaultSupervisionPropagationModeEnum.ONLY_DEFAULT),
    });
    this.autocompleteContacts();
  }

  autocompleteContacts(search = ''): void {
    this.contacts$ = this.userProfileService
      .findAll(null, null, 'fullName', 'asc', search, {
        isSupervision: true,
        isPredik: false,
        excludedIds: [this.data.contact._id],
      })
      .pipe(map(result => result.data));
  }

  confirm(): void {
    const isReplace = this.form.value.choice === 'replace';
    const newContact: DefaultSupervisionAssignableContact = this.form.value.contact;

    if (isReplace && !newContact?._id) {
      this.form.get('contact').markAsTouched();
      this.snackbarService.warning('Sélectionnez le contact de remplacement');
      return;
    }

    this.loading = true;
    this.defaultSupervisionService
      .replaceContact({
        oldContactId: this.data.contact._id,
        newContact: isReplace
          ? { _id: newContact._id, firstName: newContact.firstName, lastName: newContact.lastName }
          : undefined,
        establishmentId: this.data.establishmentId,
        propagationMode: this.form.value.propagationMode,
      })
      .subscribe({
        next: () => {
          this.snackbarService.success(
            isReplace ? 'Contact par défaut remplacé avec succès' : 'Affectations retirées avec succès',
          );
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackbarService.danger('Une erreur est survenue lors du remplacement du contact');
          this.loading = false;
        },
      });
  }
}
